import React from "react";
import { useLandingContent } from "../../lib/hooks/useContent";
import heroPhoto from "../../assets/pexels-photo-3184418.jpeg";

const PANE_GAP_REM = 8; 

type AboutData = {
  title?: string;
  description?: string;
  image?: string;
};

export default function About() {
  const { data, loading, error } = useLandingContent() as {
    data?: { about?: AboutData };
    loading?: boolean;
    error?: unknown;
  };

  const about = data?.about;

  if (loading && !about) {
    return (
      <section id="about" className="py-16 md:py-20 bg-[#080D1B]">
        <p className="text-center text-white/70">Loading about info…</p>
      </section>
    );
  }
  if (error && !about) {
    return (
      <section id="about" className="py-16 md:py-20 bg-[#080D1B]">
        <p className="text-center text-red-400">Failed to load about info.</p>
      </section>
    );
  }

  const title = (about?.title || "About Us").trim();
  const imgSrc = about?.image || heroPhoto;

  const parts = title.split(/\s+/);
  const lastWord = parts.pop() ?? "";
  const leadTitle = parts.join(" ");

  const raw = (about?.description ?? "").trim();
  const paragraphs = raw.split(/\r?\n\s*\r?\n/).filter(Boolean);

  return (
    <section id="about" className="px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-[#080D1B] text-white">
      {/* Even split: each pane width matches its inner box width */}
      <div
        className="flex flex-col md:flex-row items-center justify-center gap-y-4 mx-auto"
        style={{ columnGap: `${PANE_GAP_REM}rem` }}
      >
        {/* IMAGE PANE — pane width = box width */}
        <div className="w-full md:w-1/2 flex items-center justify-center md:justify-end">
          {/* Image box fills pane width; explicit height keeps ratio */}
          <div className="relative w-full h-[18rem] sm:h-[20rem] md:h-[22rem] lg:h-[26rem]">
            <img
              src={imgSrc}
              alt="About Bracket & Brace"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-right rounded-2xl shadow-2xl"
            />
          </div>
        </div>

        {/* TEXT PANE — pane width = text box width */}
        <div className="w-full sm:w-[32rem] md:w-1/2 flex items-center justify-center lg:mr-4">
          <div className="w-full text-center md:text-center w-[90%] md:w-[80%] lg:w-[36rem]">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold leading-tight break-words hyphens-auto text-[#dee3f2]">

              {leadTitle}{leadTitle && " "}
              <span className="text-[#2B61DE]">{lastWord}</span>
            </h1>

            {/* mt-8 per your spec */}
            <div className="mt-8 space-y-3 text-[#DEE3F2]/90">
              {paragraphs.length
                ? paragraphs.map((p, i) => (
                    <p key={i} className="leading-relaxed">{p}</p>
                  ))
                : <p className="leading-relaxed whitespace-pre-line">{raw}</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}